// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

/**
 * ActivityItemCard
 *
 * Shared row component used by `ActivityLogPage` (brokerage-wide feed) and
 * `ClientActivityTab` (client-scoped feed). Renders a single unified activity
 * item (alert or insight) with severity icon, title/description and a relative
 * timestamp, and is keyboard-accessible via Enter.
 *
 * When `selectable` is true, a leading checkbox is rendered. Toggling the
 * checkbox does not trigger the row click handler.
 */

import { Checkbox } from '@loa/ui-components';
import {
  SeverityIcon,
  severityStyles,
  insightStyle,
  formatRelativeTime,
} from '../dashboard/SeverityIcon';
import type { UnifiedActivityItem } from '../../types/activityLog';

export type ActivityItemCardProps = Readonly<{
  item: UnifiedActivityItem;
  onClick: (item: UnifiedActivityItem) => void;
  selectable?: boolean;
  selected?: boolean;
  onSelectChange?: (id: string, checked: boolean) => void;
}>;

export function ActivityItemCard({
  item,
  onClick,
  selectable = false,
  selected = false,
  onSelectChange,
}: ActivityItemCardProps) {
  const isUrgentOverride =
    item.alertType === 'INCOMPLETE_PROFILE' ||
    item.alertType === 'INCOMPLETE_PROFILE_EXTENDED';
  let iconStyle = insightStyle;
  if (item.source !== 'insight') {
    if (isUrgentOverride) iconStyle = severityStyles.URGENT;
    else iconStyle = severityStyles[item.severity ?? 'INFO'] ?? severityStyles.INFO;
  }

  return (
    <div className="activity-log__item-row">
      {selectable && (
        <div
          className="activity-log__item-checkbox"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          role="presentation"
        >
          <Checkbox
            checked={selected}
            onChange={(e) => onSelectChange?.(item.id, e.target.checked)}
            aria-label={`Select activity item: ${item.title}`}
          />
        </div>
      )}

      <button
        type="button"
        className="activity-log__item"
        onClick={() => onClick(item)}
        style={{ background: 'transparent', border: 0, textAlign: 'left', width: '100%', cursor: 'pointer' }}
      >
        <div className="activity-log__item-icon">
          <SeverityIcon style={iconStyle} size={28} />
        </div>

        <div className="activity-log__item-content">
          <div className="activity-log__item-title">{item.title}</div>
          <div className="activity-log__item-description">{item.description}</div>
        </div>

        <div className="activity-log__item-timestamp">
          {formatRelativeTime(item.timestamp)}
        </div>
      </button>
    </div>
  );
}
